/**
 * Shared visual tokens for the onboarding surfaces.
 *
 * These are the terminal's own tokens, not the marketing site's. Onboarding
 * used to be styled from the marketing hero — near-black glass, Montserrat
 * 800, an ambient bloom — on the theory that the first thing a trialist sees
 * should match what sold them. In practice it made the product's own first
 * screen look like a different application: Inter everywhere else and
 * Montserrat here, gray-900 panels everywhere else and translucent white
 * here. The handover from advert to tool is the moment to look like the tool.
 *
 * Everything below resolves to what the rest of the portal already uses:
 * gray-900 panels with gray-800 surfaces on them, gray-700 hairlines, small
 * radii, squawk-blue as the only accent.
 *
 * Three text weights and no more: gray-100 for anything being read, gray-300
 * for section labels, gray-400 for helper text. A fourth grey is a sign the
 * hierarchy is being expressed with colour instead of size and position.
 */

/**
 * Card surface.
 *
 * gray-800, not gray-900: the modal panel is itself gray-900, so a gray-900
 * card was an outline with no surface behind it — every card on step 1 read as
 * a floating border. One step up from the panel is what the rest of the portal
 * does (see WatchlistTermsModal's gray-900 panel and gray-800 chips).
 */
export const CARD = "rounded-md border border-gray-700 bg-gray-800";

/** Selected/active variant of CARD. */
export const CARD_ACTIVE = "rounded-md border border-squawk-blue/60 bg-squawk-blue/10";

/** Quiet chip used for the step counter. */
export const PILL =
  "inline-flex items-center gap-1.5 rounded border border-gray-700 bg-gray-800 px-2 py-1 text-2xs font-semibold uppercase tracking-[0.1em] text-gray-400";

/** Section label — sits above grouped content. */
export const SECTION_LABEL =
  "text-xs font-semibold uppercase tracking-wide text-gray-300";

/**
 * Helper text under a section label.
 *
 * One token because there were three variants of this — gray-400 above one
 * control, gray-400 below another, gray-500 beside a third — which is most of
 * why the step looked like it had more type styles than ideas.
 */
export const HINT = "text-xs leading-relaxed text-gray-400";

/** Screen heading. Matches the page titles elsewhere in the portal. */
export const HEADING = "text-xl font-semibold text-gray-100 sm:text-2xl";

/** Text input / textarea. */
export const INPUT =
  // Same reasoning as CARD: a gray-900 field on a gray-900 panel has no
  // surface. Placeholder lifted off gray-500, which failed contrast at this
  // size against a dark panel.
  "w-full rounded-md border border-gray-700 bg-gray-800 px-3 py-2.5 text-sm text-gray-100 " +
  "placeholder:text-gray-400 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-squawk-blue";

/**
 * Primary CTA.
 *
 * The tinted-outline blue the builder already uses for "Build it for me" —
 * the portal has no solid-fill button, so the marketing pill with its glow
 * was the only one of its kind in the product.
 */
export const CTA_PRIMARY =
  "inline-flex items-center justify-center gap-2 cursor-pointer rounded border border-squawk-blue bg-squawk-blue/15 px-4 py-2 text-sm font-semibold text-squawk-blue " +
  "transition-colors hover:bg-squawk-blue/25 disabled:cursor-not-allowed disabled:opacity-40";

/** Quiet secondary action sitting beside the primary CTA. */
export const CTA_GHOST =
  "inline-flex items-center justify-center cursor-pointer rounded border border-gray-600 bg-gray-800 px-4 py-2 text-sm font-medium text-gray-200 " +
  "transition-colors hover:border-gray-500 hover:bg-gray-700";

/** Text-only tertiary action (Back, Skip). */
export const LINK_MUTED =
  "bg-transparent border-none p-0 text-sm text-gray-500 hover:text-gray-300 transition-colors cursor-pointer";
